import * as React from 'react';
import getStore from '../store/store';
import {
    updateSettingsDialogViewState,
    updateOverriddenOptionOnSettingsDialogViewState,
    updateOverriddenCategoryOnSettingsDialogViewState,
} from '../actions/updateSettingsDialogViewState';
import { observer } from 'mobx-react-lite';
import { EditorServiceModels } from '@editor/client-rest-api';
import { Label } from '@fluentui/react/lib/Label';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { GroupedList, IGroup, IGroupHeaderProps } from '@fluentui/react/lib/GroupedList';
import { IconButton } from '@fluentui/react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import CritiqueTypeDefinition from '../store/schema/CritiqueTypeDefinition';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { groupChevronAriaLabel } from './EditorOptions.locstring.json';
import getViewStateValueForCritique from '../selectors/getViewStateValueForCritique';
import loc, { format } from 'owa-localize';
import isCategoryEnabled, {
    CATEGORY_ID_FOR_SPELLING,
    CATEGORY_ID_FOR_GRAMMAR,
} from '../utils/isCategoryEnabled';

const styles = require('./EditorOptions.scss');
let classNames = require('classnames/bind').bind(styles);

export interface CritiqueGroupsProps {
    critiqueCategoryNames: { id: string; name: string }[];
    items: CritiqueTypeDefinition[];
    enabled: boolean;
}

const CritiqueGroups = observer(function CritiqueGroups(props: CritiqueGroupsProps) {
    const [renderedGroups, setRenderedGroups] = React.useState<IGroup[]>([]);

    React.useEffect(() => {
        generateGroupForItems();
    }, [props.items]);

    const getPreviousCollapsedState = (id: string): boolean => {
        let collapsedState: boolean = true;
        renderedGroups.forEach(group => {
            if (group.key === id) {
                collapsedState = group.isCollapsed === true;
            }
        });

        return collapsedState;
    };

    const generateGroupForItems = () => {
        let runningCount = 0;
        const groups: IGroup[] = [];
        props.critiqueCategoryNames.forEach(category => {
            // key is the category id, value is the category name
            const critiqueTypesForGroup = props.items.filter(el => el.categoryId === category.id);

            let group = {
                count: critiqueTypesForGroup.length,
                key: category.id, // The key is the id of the category
                name: category.name,
                startIndex: runningCount,
                isCollapsed: getPreviousCollapsedState(category.id),
            };

            groups.push(group);
            runningCount += critiqueTypesForGroup.length;
        });

        setRenderedGroups(groups);
    };

    const onRenderGroupHeader = (groupHeaderProps: IGroupHeaderProps | undefined): JSX.Element => {
        if (!groupHeaderProps) {
            return <></>;
        }

        const toggleCollapse = (): void => {
            groupHeaderProps.onToggleCollapse!(groupHeaderProps.group!);
        };

        const iconName = groupHeaderProps.group!.isCollapsed
            ? ControlIcons.ChevronRight
            : ControlIcons.ChevronDown;

        const groupName = groupHeaderProps.group!.name;
        return (
            <div className={styles.groupHeaderDiv}>
                <div className={styles.categoryTitle}>
                    <IconButton
                        onClick={toggleCollapse}
                        aria-expanded={!groupHeaderProps.group!.isCollapsed}
                        aria-label={format(loc(groupChevronAriaLabel), groupName)}
                        disabled={!props.enabled}
                        className={classNames(
                            styles.categoryIconButton,
                            !props.enabled && styles.disabled
                        )}>
                        <Icon iconName={iconName} />
                    </IconButton>
                    <Label disabled={!props.enabled}>{groupName}</Label>
                </div>
                <div className={styles.categoryToggle}>
                    <Toggle
                        checked={getCheckedForCategory(groupHeaderProps.group!.key)}
                        onChange={getOnChangeForCategory(groupHeaderProps.group!.key)}
                        className={styles.toggleButton}
                        ariaLabel={groupName}
                        role="switch"
                        disabled={!props.enabled}
                    />
                </div>
            </div>
        );
    };

    const onRenderGroupCell = (
        nestingDepth?: number,
        item?: any,
        index?: number
    ): React.ReactNode => {
        return <div role="row">{renderCritiqueTypeDefinition(item)}</div>;
    };

    const getCheckedForCategory = (categoryId: string): boolean => {
        // We have two special categories: Spelling and Grammar that have their options stored separately because
        // they are always present regardless of language/config/license
        if (categoryId === CATEGORY_ID_FOR_SPELLING) {
            return getStore().settingsDialogViewState.spellCheckEnabled === true;
        } else if (categoryId === CATEGORY_ID_FOR_GRAMMAR) {
            return getStore().settingsDialogViewState.grammarEnabled === true;
        } else {
            const overriddenCategories = getStore().settingsDialogViewState?.overriddenCategories;
            if (overriddenCategories?.has(categoryId)) {
                return overriddenCategories.get(categoryId) === true;
            }
            return true;
        }
    };

    const renderCritiqueTypeDefinition = (
        critiqueTypeDefinition: CritiqueTypeDefinition
    ): JSX.Element => {
        const { critiqueTypeInfo, categoryId } = critiqueTypeDefinition;
        const enabled = isCategoryEnabled(categoryId);
        return (
            <div className={styles.critiqueTypeDiv} role="cell">
                {critiqueTypeInfo.isBinaryCritiqueType
                    ? renderBinaryCritiqueTypeInfo(critiqueTypeInfo, enabled)
                    : renderComplexCritiqueTypeInfo(critiqueTypeInfo, enabled)}
            </div>
        );
    };

    const renderBinaryCritiqueTypeInfo = (
        critiqueTypeInfo: EditorServiceModels.CritiqueTypeInfo,
        enabled: boolean
    ): JSX.Element => {
        return (
            <Checkbox
                label={critiqueTypeInfo.name}
                checked={getViewStateValueForCritique(critiqueTypeInfo.id) === 1}
                onChange={getOnChangeForBinaryCritiqueType(critiqueTypeInfo)}
                disabled={!enabled || !props.enabled}
            />
        );
    };

    const renderComplexCritiqueTypeInfo = (
        critiqueTypeInfo: EditorServiceModels.CritiqueTypeInfo,
        enabled: boolean
    ): JSX.Element => {
        const { name, critiqueTypeValues } = critiqueTypeInfo;
        if (!critiqueTypeValues) {
            return <></>;
        }
        return (
            <Dropdown
                label={name}
                options={critiqueTypeValues.map((value: string, index: number) => {
                    return { key: index, text: value };
                })}
                selectedKey={getViewStateValueForCritique(critiqueTypeInfo.id)}
                onChange={getOnChangeForComplexCritiqueType(critiqueTypeInfo)}
                disabled={!enabled}
            />
        );
    };

    const getOnChangeForCategory = (
        categoryId: string
    ): ((
        event?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => void) => {
        return (
            event?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
            checked?: boolean | undefined
        ): void => {
            if (categoryId === CATEGORY_ID_FOR_SPELLING) {
                updateSettingsDialogViewState({
                    spellCheckEnabled: checked,
                });
            } else if (categoryId === CATEGORY_ID_FOR_GRAMMAR) {
                updateSettingsDialogViewState({
                    grammarEnabled: checked,
                });
            } else {
                // If the category isn't spelling or grammar, then its setting is dependent on config
                updateOverriddenCategoryOnSettingsDialogViewState(categoryId, checked === true);
            }

            // Update the groups array so the GroupedList control knows it should re-render
            setRenderedGroups([...renderedGroups]);
        };
    };

    const getOnChangeForBinaryCritiqueType = (
        critiqueTypeInfo: EditorServiceModels.CritiqueTypeInfo
    ): ((
        event?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => void) => {
        return (
            event?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
            checked?: boolean | undefined
        ): void => {
            const overriddenOption: EditorServiceModels.CritiqueTypeOption = {
                id: critiqueTypeInfo.id,
                value: checked ? '1' : '0',
            };
            updateOverriddenOptionOnSettingsDialogViewState(overriddenOption);

            // Update the groups array so the GroupedList control knows it should re-render
            setRenderedGroups([...renderedGroups]);
        };
    };

    const getOnChangeForComplexCritiqueType = (
        critiqueTypeInfo: EditorServiceModels.CritiqueTypeInfo
    ): ((
        event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption | undefined,
        index?: number | undefined
    ) => void) => {
        return (
            event: React.FormEvent<HTMLDivElement>,
            option?: IDropdownOption | undefined,
            index?: number | undefined
        ): any => {
            const overriddenOption: EditorServiceModels.CritiqueTypeOption = {
                id: critiqueTypeInfo.id,
                value: option?.key?.toString() || '',
            };
            updateOverriddenOptionOnSettingsDialogViewState(overriddenOption);

            // Update the groups array so the GroupedList control knows it should re-render
            setRenderedGroups([...renderedGroups]);
        };
    };

    return (
        <GroupedList
            items={props.items}
            onRenderCell={onRenderGroupCell}
            groups={renderedGroups}
            groupProps={{
                onRenderHeader: onRenderGroupHeader,
            }}
        />
    );
});

export default CritiqueGroups;
